import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Package } from 'lucide-react';
import { Button, Select } from '~/components/ui';
import { statusFromWeeksCover, weeksCover } from '~/utils/inventory';
import { getPlatformProductDimensions, wholeFootLength } from '~/utils/order';
import { type ReactElement, useMemo, useEffect, useRef } from 'react';
import type { Inventory } from '~/graphql/graphql';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '~/redux/store';
import { setSelectedLocationId } from '~/redux/reducers/cart';
import type { Location } from '~/types';
import {
  useGetBuyerInventoriesForLengths,
  useGetPlatformProductsLengthsForProduct,
} from '~/api/inventory';
import { useDeepLinkedLocationId } from '~/hooks/useDeepLinkedLocationId';

const TALLY_LENGTHS_FT = [8, 10, 12, 14, 16, 18, 20];

type InventoryRequirementProps = {
  inventory?: Inventory | null;
  loading?: boolean;
  selectedLengthCount?: number;
  isCannedTally?: boolean;
  platformProductId?: string;
};

function getThresholds(
  urgent: number,
  monitor: number,
  target: number,
  t: (key: string) => string
): ReactElement {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-center">
        <p className="text-xs text-error-600 font-medium">
          {t('inventory.urgent')}
        </p>
        <p className="text-xs text-neut-900">
          &lt; {urgent}
          {t('inventory.weeks_short_form')}
        </p>
      </div>
      <div className="text-center">
        <p className="text-xs text-warning-600 font-medium">
          {t('inventory.monitor')}
        </p>
        <p className="text-xs text-neut-900">
          &lt; {monitor}
          {t('inventory.weeks_short_form')}
        </p>
      </div>
      <div className="text-center">
        <p className="text-xs text-success-600 font-medium">
          {t('inventory.healthy')}
        </p>
        <p className="text-xs text-neut-900">
          &ge; {target}
          {t('inventory.weeks_short_form')}
        </p>
      </div>
    </div>
  );
}

function getWeeksCovered(
  stock: number,
  usageRatePerWeek: number,
  t: (key: string) => string
): string {
  if (usageRatePerWeek === 0) {
    return t('inventory.not_applicable');
  }
  return (stock / usageRatePerWeek).toFixed(1) + ' ' + t('inventory.weeks');
}

function getAddressLabel(location: Location): string {
  return `${location.code} - ${location.name} - ${location.facility.address.addressLine1}, ${location.facility.address.city}`;
}

function getAddressOptions(locations: Location[]) {
  return locations.map((location) => ({
    label: getAddressLabel(location),
    value: location.id,
  }));
}

export default function InventoryRequirement({
  inventory,
  loading = false,
  selectedLengthCount = 0,
  isCannedTally = false,
  platformProductId,
}: Readonly<InventoryRequirementProps>) {
  const { t } = useTranslation('inventory');
  const tOb = useTranslation('order-builder').t;

  // Inventory is held per length, so the panel only has figures to show for exactly one.
  const isAllLengths = selectedLengthCount === 0;
  const isMultiLength = selectedLengthCount > 1;
  const isSingleLength = selectedLengthCount === 1;

  const urlLocationId = useDeepLinkedLocationId();

  const user = useSelector((state: RootState) => state.auth.user);
  const locations = useMemo(() => user?.locations || [], [user]);
  const selectedLocationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );
  const dispatch = useDispatch();

  const initialLocationId = urlLocationId ?? locations?.[0]?.id ?? undefined;

  // Seeds the initial selection exactly once per mount, then never touches it again — for either
  // branch below. Re-running on a later selectedLocationId change (e.g. re-checking
  // hasValidSelection) would re-apply urlLocationId or the fallback and silently revert whatever
  // the buyer just picked from the Select.
  const hasSeededLocationRef = useRef(false);

  useEffect(() => {
    if (hasSeededLocationRef.current) {
      return;
    }
    if (urlLocationId) {
      hasSeededLocationRef.current = true;
      dispatch(setSelectedLocationId(urlLocationId));
      return;
    }
    const hasValidSelection = locations.some(
      (l) => l.id === selectedLocationId
    );
    if (hasValidSelection) {
      hasSeededLocationRef.current = true;
      return;
    }
    if (initialLocationId) {
      hasSeededLocationRef.current = true;
      dispatch(setSelectedLocationId(initialLocationId));
    }
  }, [
    urlLocationId,
    initialLocationId,
    selectedLocationId,
    locations,
    dispatch,
  ]);

  return (
    <div className="bg-neut-light rounded-lg border border-neut-border shadow-sm">
      <div className="px-6 py-4 border-b border-neut-border bg-neut-50">
        <h2 className="text-sm font-semibold text-neut-900">
          {t('inventory.requirement')}
        </h2>
      </div>

      {/* Shipping Address */}
      <div className="px-6 py-4 border-b border-neut-100 bg-prim-50/50">
        <label className="block text-xs font-semibold text-prim-800 uppercase tracking-wider mb-2">
          {tOb('order_builder.shipping_address')}{' '}
          <span className="text-error-500">*</span>
        </label>
        <Select
          id="shippingAddressSelect"
          name="locationId"
          value={selectedLocationId}
          options={getAddressOptions(locations)}
          onChange={(_, value) => dispatch(setSelectedLocationId(value))}
        />
      </div>

      {/* Body */}
      {isCannedTally && (
        <CannedTallyView
          inventory={inventory ?? null}
          loading={loading}
          t={t}
          platformProductId={platformProductId}
          locationId={selectedLocationId}
        />
      )}
      {!isCannedTally && isAllLengths && (
        <LengthFilterNotice
          title={t('inventory.all_lengths_selected')}
          description={t('inventory.all_lengths_selected_description')}
        />
      )}
      {!isCannedTally && isMultiLength && (
        <LengthFilterNotice
          title={t('inventory.multiple_lengths_selected')}
          description={t('inventory.multiple_lengths_selected_description')}
        />
      )}
      {!isCannedTally && isSingleLength && !inventory && <NoInventoryView />}
      {!isCannedTally && isSingleLength && inventory && (
        <NormalView inventory={inventory} loading={loading} t={t} />
      )}
    </div>
  );
}

function NormalView({
  inventory,
  loading,
  t,
}: Readonly<{
  inventory: Inventory;
  loading: boolean;
  t: (key: string) => string;
}>) {
  const stock = inventory?.quantity.value;
  const stockUnit = inventory?.quantity.unit ?? '';
  const available = inventory?.availableStock.value;
  const onHand = inventory?.onHandStock.value;
  const isOversold = (available ?? 0) < 0;
  const usageRatePerWeek =
    inventory?.buyerInventoryConfiguration?.usageRatePerWeek?.value ?? 0;
  const targetWeeksCovered =
    inventory?.buyerInventoryConfiguration?.targetWeeksCovered ?? 0;
  const weeksCovered = getWeeksCovered(stock, usageRatePerWeek, t);
  const urgent =
    inventory?.buyerInventoryConfiguration?.weeksCoveredBeforeUrgent ?? 0;
  const monitor =
    inventory?.buyerInventoryConfiguration?.weeksCoveredBeforeMonitor ?? 0;
  const target =
    inventory?.buyerInventoryConfiguration?.targetWeeksCovered ?? 0;
  const status = statusFromWeeksCover(
    weeksCover({
      currentStock: inventory?.quantity,
      usageRatePerWeek:
        inventory?.buyerInventoryConfiguration?.usageRatePerWeek,
      dimensions: getPlatformProductDimensions(
        inventory?.platformProductsLength?.platformProduct,
        inventory?.platformProductsLength?.length,
        inventory?.platformProductsLength?.isNominal
      ),
    }),
    urgent,
    monitor,
    t
  );
  const thresholds = getThresholds(urgent, monitor, target, t);

  return (
    <div className="px-6 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-neut-600">{t('inventory.stock')}</p>
        <p
          className={`text-sm font-semibold ${isOversold ? 'text-error-700' : 'text-neut-900'}`}
          data-testid="stock-value"
        >
          {loading ? '...' : `${available} `}
          {stockUnit}
        </p>
      </div>
      {onHand !== available && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-neut-600">{t('inventory.on_hand')}</p>
          <p
            className="text-sm font-semibold text-neut-900"
            data-testid="on-hand-value"
          >
            {loading ? '...' : `${onHand} `}
            {stockUnit}
          </p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs text-neut-600">{t('inventory.usage')}</p>
        <p className="text-sm font-semibold text-neut-900">
          {loading ? '...' : `${usageRatePerWeek} `}
          {`${inventory.buyerInventoryConfiguration?.usageRatePerWeek?.unit ?? stockUnit}/${t('inventory.week')}`}
        </p>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-neut-600">{t('inventory.target_cover')}</p>
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-neut-900">
            {loading ? '...' : targetWeeksCovered + ' ' + t('inventory.weeks')}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-neut-600">{t('inventory.cover')}</p>
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-neut-900">
            {loading ? '...' : weeksCovered}
          </p>
          {status}
        </div>
      </div>
      <div className="pt-3 border-t border-neut-border space-y-2">
        <p className="text-xs font-medium text-neut-700">
          {t('inventory.thresholds')}
        </p>
        {thresholds}
      </div>
    </div>
  );
}

type LengthHealth = 'urgent' | 'monitor' | 'healthy' | 'none';

function getLengthHealth(
  stock: number | null | undefined,
  usageRatePerWeek: number | null | undefined,
  urgent: number | null | undefined,
  monitor: number | null | undefined
): LengthHealth {
  if (stock == null || !usageRatePerWeek) return 'none';
  const weeksCovered = stock / usageRatePerWeek;
  if (urgent != null && weeksCovered <= urgent) return 'urgent';
  if (monitor != null && weeksCovered <= monitor) return 'monitor';
  return 'healthy';
}

const HEALTH_CLASSES: Record<
  LengthHealth,
  { bg: string; ftColor: string; stockColor: string; bar: string }
> = {
  urgent: {
    bg: 'bg-error-100',
    ftColor: 'text-error-500',
    stockColor: 'text-error-700',
    bar: 'bg-error-300',
  },
  monitor: {
    bg: 'bg-warning-50',
    ftColor: 'text-warning-500',
    stockColor: 'text-warning-700',
    bar: 'bg-warning-300',
  },
  healthy: {
    bg: 'bg-success-50',
    ftColor: 'text-success-600',
    stockColor: 'text-success-700',
    bar: 'bg-success-300',
  },
  none: {
    bg: 'bg-neut-50',
    ftColor: 'text-neut-500',
    stockColor: 'text-neut-700',
    bar: 'bg-neut-300',
  },
};

function CannedTallyView({
  loading,
  t,
  platformProductId,
  locationId,
}: Readonly<{
  inventory: Inventory | null;
  loading: boolean;
  t: (key: string) => string;
  platformProductId?: string;
  locationId?: string | null;
}>) {
  const { t: tOb } = useTranslation('order-builder');

  const { data: lengthsData } = useGetPlatformProductsLengthsForProduct(
    platformProductId ?? '',
    { enabled: !!platformProductId }
  );

  const ftToLengthId = useMemo(() => {
    const map = new Map<number, string>();
    for (const ppl of lengthsData?.platformProductsLengthsForProduct ?? []) {
      const ft = wholeFootLength(ppl.length?.actualLengthInches ?? 0);
      if (ft !== null && ppl.length && !map.has(ft)) {
        map.set(ft, ppl.length.id);
      }
    }
    return map;
  }, [lengthsData]);

  const orderedLengthIds = useMemo(
    () => TALLY_LENGTHS_FT.map((ft) => ftToLengthId.get(ft) ?? ''),
    [ftToLengthId]
  );

  const inventoryQueries = useGetBuyerInventoriesForLengths(
    orderedLengthIds,
    platformProductId,
    locationId
  );

  const usageRatePerWeek = inventoryQueries.reduce((sum, q) => {
    return (
      sum +
      (q?.data?.buyerInventoryByProductAndLocation?.buyerInventoryConfiguration
        ?.usageRatePerWeek?.value ?? 0)
    );
  }, 0);
  const usageRateUnit = inventoryQueries.find((q) => q.data)?.data
    ?.buyerInventoryByProductAndLocation?.buyerInventoryConfiguration
    ?.usageRatePerWeek?.unit;

  let usageDisplay: string;
  if (loading) {
    usageDisplay = '...';
  } else if (usageRatePerWeek > 0) {
    usageDisplay = `${usageRatePerWeek.toFixed(3)} ${usageRateUnit}/${t('inventory.week')}`;
  } else {
    usageDisplay = '—';
  }

  return (
    <div className="px-6 py-4 space-y-3">
      <div className="pb-2 border-b border-neut-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-neut-600">
            {tOb('order_builder.filters.mode')}
          </p>
          <p className="text-sm font-semibold text-neut-900">
            {tOb('order_builder.filters.canned_tally')}
          </p>
        </div>
        <p className="text-xs font-medium text-neut-700 mb-1.5">
          {tOb('order_builder.filters.stock_by_length')}
        </p>
        <div className="flex flex-wrap gap-1 text-xs text-center">
          {TALLY_LENGTHS_FT.map((ft, i) => {
            const q = inventoryQueries[i];
            const inv = q?.data?.buyerInventoryByProductAndLocation;
            const stockValue = inv?.quantity?.value;
            const stockUnit = inv?.quantity?.unit ?? '';
            const usageRate =
              inv?.buyerInventoryConfiguration?.usageRatePerWeek?.value;
            const urgent =
              inv?.buyerInventoryConfiguration?.weeksCoveredBeforeUrgent;
            const monitor =
              inv?.buyerInventoryConfiguration?.weeksCoveredBeforeMonitor;
            const isLengthLoading =
              loading || (!!orderedLengthIds[i] && q?.isLoading);
            const health = getLengthHealth(
              stockValue,
              usageRate,
              urgent,
              monitor
            );
            const hc = HEALTH_CLASSES[health];
            let stockDisplay: string | number = '—';
            if (isLengthLoading) {
              stockDisplay = '...';
            } else if (stockValue != null) {
              stockDisplay = stockValue;
            }
            return (
              <div key={ft} className={`${hc.bg} rounded px-1 py-1 min-w-12`}>
                <div className={`${hc.ftColor} font-medium`}>
                  {ft}
                  {tOb('order_builder.canned_tally_calculator.ft')}
                </div>
                <div className={hc.stockColor}>{stockDisplay}</div>
                <div className={`h-0.5 mt-1 rounded-full ${hc.bar}`} />
                <div className="text-[10px] text-neut-400 mt-0.5">
                  {stockUnit || '—'}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-neut-400 mt-1 text-right">
          {tOb('order_builder.filters.total_stock_per_location')}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-neut-600">{t('inventory.usage')}</p>
        <p className="text-sm font-semibold text-neut-900">{usageDisplay}</p>
      </div>
    </div>
  );
}

const NoInventoryView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('inventory');

  return (
    <div className="text-center space-y-4 p-3">
      <div className="mx-auto w-12 h-12 bg-status-info-50 rounded-full flex items-center justify-center">
        <CircleAlert
          size={24}
          className="text-status-info-600"
          aria-hidden="true"
        />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-neut-900 mb-1">
          {t('inventory.not_set_up')}
        </h3>
        <p className="text-xs text-neut-600 mb-4">
          {t('inventory.not_set_up_description')}
        </p>
      </div>
      <Button variant="primary" onClick={() => navigate(`/buyer-inventory`)}>
        <Package size={16} className="mr-2" aria-hidden="true" />
        {t('inventory.set_up_now')}
      </Button>
    </div>
  );
};

const LengthFilterNotice = ({
  title,
  description,
}: Readonly<{ title: string; description: string }>) => (
  <div className="text-center space-y-4 p-4">
    <div className="mx-auto w-12 h-12 bg-status-info-50 rounded-full flex items-center justify-center">
      <CircleAlert
        size={24}
        className="text-status-info-600"
        aria-hidden="true"
      />
    </div>
    <div>
      <h3 className="text-sm font-semibold text-neut-900 mb-1">{title}</h3>
      <p className="text-xs text-neut-600 mb-4">{description}</p>
    </div>
  </div>
);
